/**
 * The sections of a well servicing job's page (WS-2, fieldiq-api#1282): the
 * BOP stack, owned equipment, rent-ins and the tour-sheet list. Each is a
 * full-width panel; an add form opens inline in the panel (a row is a child of
 * the job), and a removal asks in-app first.
 *
 * Writes are WS_DISPATCH. A closed job takes no writes (409 job_closed), so its
 * forms are not offered. A refusal that still arrives reads as one sentence
 * from wsJobRefusal, never as a green toast.
 *
 * THE BOP STACK IS SET ONLY THROUGH THE SHARED PRESSURE-CONTROL GUARD. This
 * screen offers the tenant's pressure-control components and shows each one's
 * certificate state as the register reports it, but it decides nothing: the
 * API runs the guard against the planned end and refuses (bop_stack_refused)
 * with one reason per failing component, each shown as sent. A due-soon
 * warning is shown beside the saved stack, not swallowed.
 *
 * RENT-IN COST (rate, billable, PO) is drawn only when canSeeCost — the roles
 * the API sends it to.
 */
import React, { useMemo, useState } from 'react';
import { CalendarCheck, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { usePcComponents } from '../../hooks/usePressureControl';
import { useWsRig } from '../../hooks/useWellServicing';
import {
  useAddWsJobEquipment,
  useClearWsBopStack,
  useCreateWsRentIn,
  useOpenWsTour,
  useRemoveWsJobEquipment,
  useRemoveWsRentIn,
  useSetWsBopStack,
  useUpdateWsJobEquipment,
  useUpdateWsRentIn,
  useWsTours,
} from '../../hooks/useWsJobs';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  blankToNull,
  bopRefusals,
  existingTour,
  fmtWsNumber,
  wsDay,
  wsHours,
  wsJobRefusal,
  wsRentStatusSkin,
  wsTourStatusSkin,
} from '../../lib/well-servicing';
import type { WsBopWarning, WsCatalog, WsJobDetail, WsJobEquipment, WsRentIn, WsTourSummary } from '../../types/well-servicing';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  WS_DATE_INPUT,
  WS_INPUT,
  WS_PRIMARY,
  WS_SECONDARY,
  WsBadge,
  WsDialog,
  WsEmpty,
  WsField,
  WsFormError,
  WsLoading,
  WsPanel,
  WsRegister,
  WsSpec,
  type WsColumn,
} from './WsBits';

const SETTLED = { isLoading: false };

function RemoveConfirm({
  open, title, body, confirmLabel, pending, error, onClose, onConfirm,
}: {
  open: boolean; title: string; body: string; confirmLabel: string; pending: boolean; error: string | null;
  onClose: () => void; onConfirm: () => void;
}) {
  return (
    <WsDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WS_SECONDARY} onClick={onClose} disabled={pending}>
            Keep it
          </button>
          <button type="button" className={WS_PRIMARY} onClick={onConfirm} disabled={pending}>
            {pending ? 'Removing…' : confirmLabel}
          </button>
        </>
      }
    >
      <WsFormError message={error} />
      <p className="text-sm text-fieldiq-text">{body}</p>
    </WsDialog>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={WS_SECONDARY} aria-label={label} onClick={onClick}>
      <Trash2 className="size-4" aria-hidden="true" />
      Remove
    </button>
  );
}

function AddButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={WS_PRIMARY} onClick={onClick} disabled={disabled}>
      <Plus className="size-4" aria-hidden="true" />
      {label}
    </button>
  );
}

function FormButtons({ pending, onCancel, label }: { pending: boolean; onCancel: () => void; label: string }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" className={WS_SECONDARY} onClick={onCancel} disabled={pending}>
        Cancel
      </button>
      <button type="submit" className={WS_PRIMARY} disabled={pending}>
        {pending ? 'Saving…' : label}
      </button>
    </div>
  );
}

function DateDialog({
  open, title, label, initial, body, pending, error, onClose, onSave,
}: {
  open: boolean; title: string; label: string; initial: string; body: string; pending: boolean; error: string | null;
  onClose: () => void; onSave: (value: string | null) => void;
}) {
  const [value, setValue] = useState(initial);
  React.useEffect(() => {
    if (open) setValue(initial);
  }, [open, initial]);
  return (
    <WsDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WS_SECONDARY} onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="button" className={WS_PRIMARY} onClick={() => onSave(value || null)} disabled={pending}>
            {pending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <WsFormError message={error} />
      <p className="mb-3 text-sm text-fieldiq-text">{body}</p>
      <WsField label={label}>
        <input type="date" className={WS_DATE_INPUT} aria-label={label} value={value} onChange={(e) => setValue(e.target.value)} />
      </WsField>
    </WsDialog>
  );
}

// ─── BOP stack ───────────────────────────────────────────────────────────────

export function BopStackSection({ job, canWrite }: { job: WsJobDetail; canWrite: boolean }) {
  const [picking, setPicking] = useState(false);
  const components = usePcComponents(canWrite && picking);
  const set = useSetWsBopStack();
  const clear = useClearWsBopStack();
  const [pick, setPick] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refusals, setRefusals] = useState<string[] | null>(null);
  const [warnings, setWarnings] = useState<WsBopWarning[]>([]);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  // A stack is a top-level component (no parent); its parts ride with it.
  const stacks = (components.data?.rows ?? []).filter((c) => !c.spec.stackParentId);
  const failure = queryLoadFailure(components);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRefusals(null);
    if (!pick) return setError('Choose the BOP stack.');
    set.mutate(
      { id: job.id, equipmentId: pick },
      {
        onSuccess: (res) => {
          setWarnings(res.warnings);
          toast.success('BOP stack set');
          setPicking(false);
          setPick('');
        },
        onError: (err) => {
          const r = bopRefusals(err);
          if (r) setRefusals(r);
          else setError(wsJobRefusal(err, 'The BOP stack could not be set.'));
        },
      },
    );
  };

  return (
    <WsPanel
      eyebrow="Pressure control"
      title="BOP stack"
      testId="ws-job-bop"
      actions={
        canWrite && !picking ? (
          <button type="button" className={WS_PRIMARY} onClick={() => { setError(null); setRefusals(null); setPicking(true); }}>
            <ShieldCheck className="size-4" aria-hidden="true" />
            {job.bopStackEquipmentId ? 'Change stack' : 'Set stack'}
          </button>
        ) : undefined
      }
    >
      <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3">
        <WsSpec label="Stack" value={job.bopStackName || '—'} />
        <WsSpec label="Set" value={job.bopStackSetAt ? wsDay(job.bopStackSetAt.slice(0, 10)) : '—'} />
        <WsSpec label="Checked against" value={job.plannedEndOn ? `Planned end ${wsDay(job.plannedEndOn)}` : 'Today (no planned end)'} />
      </dl>
      {warnings.length > 0 && (
        <ul className="space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm text-fi-amber" data-testid="ws-bop-warnings">
          {warnings.map((w, i) => (
            <li key={`${w.equipment_id}-${i}`}>{w.message}</li>
          ))}
        </ul>
      )}
      {picking && (
        <form onSubmit={submit} className="space-y-3 border-t border-fieldiq-border px-4 py-4" data-testid="ws-bop-form">
          <WsFormError message={error} />
          {refusals && (
            <div role="alert" className="rounded-fi-control border border-fi-red/60 bg-fi-red/10 px-3 py-2 text-sm text-fieldiq-text" data-testid="ws-bop-refused">
              <p className="font-bold">The pressure-control check refused this stack:</p>
              <ul className="mt-1 list-disc pl-5">
                {refusals.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {components.isLoading ? (
            <WsLoading label="Loading pressure-control components" />
          ) : failure ? (
            <LoadFailureNotice error={queryFailureError(components)} kind={failure} subject="the pressure-control components" />
          ) : stacks.length === 0 ? (
            <WsEmpty>No pressure-control components are registered. Add the stack under Pressure control first.</WsEmpty>
          ) : (
            <WsField label="Stack" hint="The certificate state is the register's; the API runs the check">
              <select className={WS_INPUT} aria-label="BOP stack" value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="">Choose…</option>
                {stacks.map((c) => (
                  <option key={c.equipmentId} value={c.equipmentId}>
                    {c.name}
                    {c.unitNumber ? ` · ${c.unitNumber}` : ''}
                    {c.certState?.status ? ` · ${String(c.certState.status).replace(/_/g, ' ')}` : ''}
                    {c.certState?.expiresOn ? ` to ${wsDay(c.certState.expiresOn)}` : ''}
                  </option>
                ))}
              </select>
            </WsField>
          )}
          <FormButtons pending={set.isPending} onCancel={() => setPicking(false)} label="Set stack" />
        </form>
      )}
      {canWrite && job.bopStackEquipmentId && !picking && (
        <div className="flex justify-end border-t border-fieldiq-border px-4 py-3">
          <RemoveButton label="Take the BOP stack off the job" onClick={() => { setClearError(null); setClearing(true); }} />
        </div>
      )}
      <RemoveConfirm
        open={clearing}
        title="Take the stack off"
        body={`Takes ${job.bopStackName ?? 'the BOP stack'} off this job.`}
        confirmLabel="Take off"
        pending={clear.isPending}
        error={clearError}
        onClose={() => setClearing(false)}
        onConfirm={() =>
          clear.mutate(job.id, {
            onSuccess: () => {
              toast.success('BOP stack taken off the job');
              setWarnings([]);
              setClearing(false);
            },
            onError: (err) => setClearError(wsJobRefusal(err, 'The BOP stack could not be taken off.')),
          })
        }
      />
    </WsPanel>
  );
}

// ─── Equipment ───────────────────────────────────────────────────────────────

export function EquipmentSection({
  job, equipment, canWrite,
}: {
  job: WsJobDetail;
  equipment: { id: string; name: string; unitNumber: string | null }[];
  canWrite: boolean;
}) {
  const add = useAddWsJobEquipment();
  const update = useUpdateWsJobEquipment();
  const remove = useRemoveWsJobEquipment();
  const [adding, setAdding] = useState(false);
  const blank = { equipment_id: '', on_date: '', notes: '' };
  const [form, setForm] = useState(blank);
  const [error, setError] = useState<string | null>(null);
  const [offing, setOffing] = useState<WsJobEquipment | null>(null);
  const [offError, setOffError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<WsJobEquipment | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const onJob = useMemo(() => new Set(job.equipment.map((e) => e.equipmentId)), [job.equipment]);
  const pickable = equipment.filter((e) => !onJob.has(e.id));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.equipment_id) return setError('Choose the unit.');
    add.mutate(
      { jobId: job.id, body: { equipment_id: form.equipment_id, on_date: blankToNull(form.on_date), notes: blankToNull(form.notes) } },
      {
        onSuccess: () => {
          toast.success('Unit added to the job');
          setForm(blank);
          setAdding(false);
        },
        onError: (err) => setError(wsJobRefusal(err, 'The unit could not be added.')),
      },
    );
  };

  const columns: WsColumn<WsJobEquipment>[] = [
    {
      key: 'unit',
      header: 'Unit',
      render: (e) => (
        <>
          <p className="font-extrabold text-white">{e.equipmentName || '—'}</p>
          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{e.unitNumber || e.serialNumber || '—'}</p>
        </>
      ),
    },
    { key: 'type', header: 'Type', render: (e) => e.equipmentType || '—' },
    { key: 'dates', header: 'On / off', render: (e) => <span className="whitespace-nowrap">{`${wsDay(e.onDate)} → ${wsDay(e.offDate)}`}</span> },
    { key: 'notes', header: 'Notes', render: (e) => e.notes || '—' },
  ];
  if (canWrite)
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      render: (e) => (
        <span className="inline-flex gap-2 whitespace-nowrap">
          <button type="button" className={WS_SECONDARY} aria-label={`Set off date for ${e.equipmentName ?? 'unit'}`} onClick={() => { setOffError(null); setOffing(e); }}>
            <CalendarCheck className="size-4" aria-hidden="true" />
            Off date
          </button>
          <RemoveButton label={`Remove ${e.equipmentName ?? 'unit'}`} onClick={() => { setRemoveError(null); setRemoving(e); }} />
        </span>
      ),
    });

  return (
    <WsPanel
      eyebrow="Equipment"
      title="Owned equipment on the job"
      testId="ws-job-equipment"
      actions={canWrite && !adding ? <AddButton label="Add unit" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ws-equipment-form">
          <WsFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-3">
            <WsField label="Unit">
              <select className={WS_INPUT} aria-label="Unit" value={form.equipment_id} onChange={(e) => setForm({ ...form, equipment_id: e.target.value })}>
                <option value="">Choose…</option>
                {pickable.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                    {u.unitNumber ? ` · ${u.unitNumber}` : ''}
                  </option>
                ))}
              </select>
            </WsField>
            <WsField label="On date">
              <input type="date" className={WS_DATE_INPUT} aria-label="On date" value={form.on_date} onChange={(e) => setForm({ ...form, on_date: e.target.value })} />
            </WsField>
            <WsField label="Notes">
              <input className={WS_INPUT} aria-label="Equipment notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </WsField>
          </div>
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add to job" />
        </form>
      )}
      <WsRegister
        query={SETTLED}
        rows={job.equipment}
        columns={columns}
        rail={() => 'border-l-transparent'}
        subject="the job's equipment"
        testId="ws-job-unit"
        minWidth="min-w-[760px]"
        empty={<WsEmpty>No owned equipment is on this job yet. The rig itself is on the job through its rig number.</WsEmpty>}
        card={(e) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-white">{e.equipmentName || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {e.equipmentType || '—'} · {wsDay(e.onDate)} → {wsDay(e.offDate)}
              </p>
            </div>
            {canWrite && <RemoveButton label={`Remove ${e.equipmentName ?? 'unit'}`} onClick={() => { setRemoveError(null); setRemoving(e); }} />}
          </div>
        )}
      />
      <DateDialog
        open={!!offing}
        title="Off date"
        label="Off date"
        initial={offing?.offDate ?? ''}
        body={`The last day ${offing?.equipmentName ?? 'this unit'} is on the job. Blank means it is still on.`}
        pending={update.isPending}
        error={offError}
        onClose={() => setOffing(null)}
        onSave={(value) =>
          offing &&
          update.mutate(
            { id: offing.id, body: { off_date: value } },
            {
              onSuccess: () => {
                toast.success('Off date saved');
                setOffing(null);
              },
              onError: (err) => setOffError(wsJobRefusal(err, 'The off date could not be saved.')),
            },
          )
        }
      />
      <RemoveConfirm
        open={!!removing}
        title="Take off the job"
        body={`Takes ${removing?.equipmentName ?? 'this unit'} off the job. A unit with hours on a tour sheet cannot be removed — set its off date instead.`}
        confirmLabel="Remove"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Taken off the job');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(wsJobRefusal(err, 'The unit could not be removed.')),
          })
        }
      />
    </WsPanel>
  );
}

// ─── Rent-ins ────────────────────────────────────────────────────────────────

export function RentInsSection({
  job, catalog, currency, canWrite, canSeeCost,
}: {
  job: WsJobDetail;
  catalog: WsCatalog | null;
  currency: string | null;
  canWrite: boolean;
  canSeeCost: boolean;
}) {
  const add = useCreateWsRentIn();
  const update = useUpdateWsRentIn();
  const remove = useRemoveWsRentIn();
  const [adding, setAdding] = useState(false);
  const blank = {
    vendor_name: '', vendor_contact: '', description: '', unit_number: '', on_rent_on: '', off_rent_on: '',
    cost_rate: '', rate_unit: 'day', billable: true, po_number: '', notes: '',
  };
  const [form, setForm] = useState(blank);
  const [error, setError] = useState<string | null>(null);
  const [offing, setOffing] = useState<WsRentIn | null>(null);
  const [offError, setOffError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<WsRentIn | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const rateUnits = catalog?.rateUnits ?? [];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.vendor_name.trim()) return setError('Enter the vendor.');
    if (!form.description.trim()) return setError('Say what was rented.');
    if (!form.on_rent_on) return setError('Enter the on-rent date.');
    if (form.off_rent_on && form.off_rent_on < form.on_rent_on) return setError('The off-rent date cannot be before the on-rent date.');
    if (canSeeCost && form.cost_rate.trim() && !/^\d+(\.\d+)?$/.test(form.cost_rate.trim())) return setError('The rate must be a number, or blank.');
    add.mutate(
      {
        jobId: job.id,
        body: {
          vendor_name: form.vendor_name.trim(),
          vendor_contact: blankToNull(form.vendor_contact),
          description: form.description.trim(),
          unit_number: blankToNull(form.unit_number),
          on_rent_on: form.on_rent_on,
          off_rent_on: blankToNull(form.off_rent_on),
          notes: blankToNull(form.notes),
          // Cost is only sent by the roles that can see it.
          ...(canSeeCost
            ? { cost_rate: blankToNull(form.cost_rate), ...(form.cost_rate.trim() ? { rate_unit: form.rate_unit } : {}), billable: form.billable, po_number: blankToNull(form.po_number) }
            : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success('Rent-in added');
          setForm(blank);
          setAdding(false);
        },
        onError: (err) => setError(wsJobRefusal(err, 'The rent-in could not be added.')),
      },
    );
  };

  const columns: WsColumn<WsRentIn>[] = [
    {
      key: 'what',
      header: 'Rented',
      render: (r) => (
        <>
          <p className="font-extrabold text-white">{r.description || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {r.vendorName || '—'}
            {r.unitNumber ? ` · ${r.unitNumber}` : ''}
          </p>
        </>
      ),
    },
    { key: 'dates', header: 'On / off rent', render: (r) => <span className="whitespace-nowrap">{`${wsDay(r.onRentOn)} → ${wsDay(r.offRentOn)}`}</span> },
    { key: 'days', header: 'Days', align: 'right', render: (r) => displayValue(r.onRentDays ?? undefined) },
    { key: 'hours', header: 'Hours', align: 'right', render: (r) => fmtWsNumber(r.loggedHours) },
  ];
  if (canSeeCost)
    columns.push(
      {
        key: 'rate',
        header: `Rate${currency ? ` (${currency})` : ''}`,
        align: 'right',
        render: (r) => (r.costRate == null ? '—' : `${fmtWsNumber(r.costRate)}${r.rateUnitLabel ? ` ${r.rateUnitLabel.toLowerCase()}` : ''}`),
      },
      { key: 'billable', header: 'Billable', render: (r) => (r.billable == null ? '—' : r.billable ? 'Yes' : 'No') },
      { key: 'po', header: 'PO', render: (r) => <span className="font-mono text-sm">{r.poNumber || '—'}</span> },
    );
  columns.push({ key: 'status', header: 'Status', render: (r) => <WsBadge skin={wsRentStatusSkin(r.rentStatus)} value={r.rentStatus} /> });
  if (canWrite)
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <span className="inline-flex gap-2 whitespace-nowrap">
          <button type="button" className={WS_SECONDARY} aria-label={`Set off-rent date for ${r.description}`} onClick={() => { setOffError(null); setOffing(r); }}>
            <CalendarCheck className="size-4" aria-hidden="true" />
            Off rent
          </button>
          <RemoveButton label={`Remove ${r.description}`} onClick={() => { setRemoveError(null); setRemoving(r); }} />
        </span>
      ),
    });

  return (
    <WsPanel
      eyebrow="Rent-ins"
      title="Rented-in units"
      testId="ws-job-rent-ins"
      actions={canWrite && !adding ? <AddButton label="Add rent-in" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ws-rent-in-form">
          <WsFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <WsField label="Vendor">
              <input className={WS_INPUT} aria-label="Vendor" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} />
            </WsField>
            <WsField label="Vendor contact">
              <input className={WS_INPUT} aria-label="Vendor contact" value={form.vendor_contact} onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} />
            </WsField>
            <WsField label="What">
              <input className={WS_INPUT} aria-label="What was rented" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </WsField>
            <WsField label="Unit number">
              <input className={WS_INPUT} aria-label="Rent-in unit number" value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} />
            </WsField>
            <WsField label="On rent">
              <input type="date" className={WS_DATE_INPUT} aria-label="On rent" value={form.on_rent_on} onChange={(e) => setForm({ ...form, on_rent_on: e.target.value })} />
            </WsField>
            <WsField label="Off rent">
              <input type="date" className={WS_DATE_INPUT} aria-label="Off rent" value={form.off_rent_on} onChange={(e) => setForm({ ...form, off_rent_on: e.target.value })} />
            </WsField>
            {canSeeCost && (
              <>
                <WsField label={`Rate${currency ? ` (${currency})` : ''}`}>
                  <div className="flex gap-2">
                    <input className={WS_INPUT} inputMode="decimal" aria-label="Rate" value={form.cost_rate} onChange={(e) => setForm({ ...form, cost_rate: e.target.value })} />
                    <select className={`${WS_INPUT} w-32 shrink-0`} aria-label="Rate unit" value={form.rate_unit} onChange={(e) => setForm({ ...form, rate_unit: e.target.value })}>
                      {rateUnits.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </WsField>
                <WsField label="PO number">
                  <input className={WS_INPUT} aria-label="Rent-in PO number" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} />
                </WsField>
                <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
                  <input type="checkbox" checked={form.billable} onChange={(e) => setForm({ ...form, billable: e.target.checked })} />
                  Billable to the customer
                </label>
              </>
            )}
          </div>
          <WsField label="Notes">
            <input className={WS_INPUT} aria-label="Rent-in notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </WsField>
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add rent-in" />
        </form>
      )}
      <WsRegister
        query={SETTLED}
        rows={job.rentIns}
        columns={columns}
        rail={(r) => wsRentStatusSkin(r.rentStatus).rail}
        subject="the rent-ins"
        testId="ws-rent-in"
        minWidth={canSeeCost ? 'min-w-[900px]' : 'min-w-[640px]'}
        empty={<WsEmpty>Nothing is rented in for this job.</WsEmpty>}
        card={(r) => (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-extrabold text-white">{r.description || '—'}</p>
                <p className="mt-0.5 text-xs text-fieldiq-muted">
                  {r.vendorName || '—'} · {wsDay(r.onRentOn)} → {wsDay(r.offRentOn)}
                </p>
              </div>
              <WsBadge skin={wsRentStatusSkin(r.rentStatus)} value={r.rentStatus} />
            </div>
            {canSeeCost && r.costRate != null && (
              <p className="mt-2 text-xs text-fieldiq-text">
                Rate {fmtWsNumber(r.costRate)} {r.rateUnitLabel?.toLowerCase() ?? ''} · {r.billable ? 'Billable' : 'Not billable'}
              </p>
            )}
          </>
        )}
      />
      <DateDialog
        open={!!offing}
        title="Off-rent date"
        label="Off rent"
        initial={offing?.offRentOn ?? ''}
        body={`The last day ${offing?.description ?? 'this unit'} is on rent. Blank means it is still on rent.`}
        pending={update.isPending}
        error={offError}
        onClose={() => setOffing(null)}
        onSave={(value) =>
          offing &&
          update.mutate(
            { id: offing.id, body: { off_rent_on: value } },
            {
              onSuccess: () => {
                toast.success('Off-rent date saved');
                setOffing(null);
              },
              onError: (err) => setOffError(wsJobRefusal(err, 'The off-rent date could not be saved.')),
            },
          )
        }
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove rent-in"
        body={`Removes ${removing?.description ?? 'this rent-in'} from the job. One with hours on a tour sheet cannot be removed — set its off-rent date instead.`}
        confirmLabel="Remove"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Rent-in removed');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(wsJobRefusal(err, 'The rent-in could not be removed.')),
          })
        }
      />
    </WsPanel>
  );
}

// ─── Tour sheets ─────────────────────────────────────────────────────────────

/**
 * The job's tour sheets, newest first. "Open tour sheet" asks for the day
 * (default the tenant's today) and, optionally, a crew from the rig to copy
 * on; the API sets the window from the tenant's tour settings. A rig that
 * already has a sheet that day is answered with a link to that sheet.
 */
export function ToursSection({
  job, today, canOpen, onOpen,
}: {
  job: WsJobDetail;
  today: string | null;
  canOpen: boolean;
  onOpen: (tourId: string) => void;
}) {
  const query = useWsTours(job.id);
  const tours = query.data?.data ?? [];
  const rig = useWsRig(canOpen && job.rigId ? job.rigId : undefined);
  const crews = rig.data?.data?.crews ?? [];
  const create = useOpenWsTour();
  const [opening, setOpening] = useState(false);
  const [workDate, setWorkDate] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setExisting(null);
    if (!workDate) return setError('Pick the day.');
    if (today && workDate > today) return setError(`A tour sheet cannot be for a future day. Today is ${wsDay(today)}.`);
    create.mutate(
      { jobId: job.id, body: { work_date: workDate, ...(templateId ? { crew_template_id: templateId } : {}) } },
      {
        onSuccess: (res) => {
          toast.success(`Tour sheet opened for ${wsDay(workDate)}`);
          setOpening(false);
          if (res.data?.id) onOpen(res.data.id);
        },
        onError: (err) => {
          const dup = existingTour(err);
          if (dup) {
            setError(dup.message);
            setExisting(dup.id);
          } else setError(wsJobRefusal(err, 'The tour sheet could not be opened.'));
        },
      },
    );
  };

  const columns: WsColumn<WsTourSummary>[] = [
    { key: 'day', header: 'Day', render: (t) => <span className="whitespace-nowrap font-extrabold text-white">{wsDay(t.workDate)}</span> },
    { key: 'window', header: 'Tour', render: (t) => <span className="whitespace-nowrap font-mono">{t.startTime && t.endTime ? `${t.startTime}–${t.endTime}` : '—'}</span> },
    { key: 'hours', header: 'Sheet', align: 'right', render: (t) => wsHours(t.tourHours) },
    { key: 'logged', header: 'Activities', align: 'right', render: (t) => wsHours(t.activityHours) },
    { key: 'covered', header: 'Covered', render: (t) => (t.complete ? 'End to end' : 'Gaps') },
    { key: 'crew', header: 'Crew', align: 'right', render: (t) => displayValue(t.crewCount ?? undefined) },
    { key: 'status', header: 'Status', render: (t) => <WsBadge skin={wsTourStatusSkin(t.status, t.statusLabel)} value={t.status} /> },
  ];

  return (
    <WsPanel
      eyebrow="Tour sheets"
      title="Daily tour sheets"
      testId="ws-job-tours"
      actions={
        canOpen && !opening ? (
          <AddButton label="Open tour sheet" onClick={() => { setError(null); setExisting(null); setWorkDate(today ?? ''); setOpening(true); }} />
        ) : undefined
      }
    >
      {opening && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ws-open-tour-form">
          <WsFormError message={error} />
          {existing && (
            <button type="button" className={WS_SECONDARY} onClick={() => onOpen(existing)}>
              Open that sheet
            </button>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <WsField label="Day">
              <input type="date" className={WS_DATE_INPUT} aria-label="Tour day" max={today ?? undefined} value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
            </WsField>
            <WsField label="Copy crew from" hint="The rig's crews; the sheet can be changed after">
              <select className={WS_INPUT} aria-label="Copy crew from" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">{rig.isLoading ? 'Loading…' : crews.length ? 'Do not copy a crew' : 'The rig has no crew yet'}</option>
                {crews.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {c.members.length} {c.members.length === 1 ? 'hand' : 'hands'}
                  </option>
                ))}
              </select>
            </WsField>
          </div>
          <FormButtons pending={create.isPending} onCancel={() => setOpening(false)} label="Open sheet" />
        </form>
      )}
      <WsRegister
        query={query}
        rows={tours}
        columns={columns}
        rail={(t) => wsTourStatusSkin(t.status).rail}
        onOpen={(t) => onOpen(t.id)}
        subject="the tour sheets"
        testId="ws-tour"
        minWidth="min-w-[760px]"
        empty={<WsEmpty>{job.rigId ? 'No tour sheet has been opened on this job yet.' : 'This job has no rig yet. Name the rig before opening a tour sheet.'}</WsEmpty>}
        card={(t) => (
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-extrabold text-white">{wsDay(t.workDate)}</p>
              <p className="mt-1 text-xs text-fieldiq-text">
                {t.startTime && t.endTime ? `${t.startTime}–${t.endTime}` : '—'} · {wsHours(t.activityHours)} of {wsHours(t.tourHours)} · Crew{' '}
                {displayValue(t.crewCount ?? undefined)}
              </p>
            </div>
            <WsBadge skin={wsTourStatusSkin(t.status, t.statusLabel)} value={t.status} />
          </div>
        )}
      />
    </WsPanel>
  );
}
